<template>
  <div class="container">
    <!-- Balance Notice -->
    <section v-if="showBalanceNote" class="center mt-25">
      <div class="balance-note">
        <i class="fas fa-info-circle grey-text"></i>
        <template v-if="balanceStatus === 'zero'">
          Balance due on this invoice is {{ 0 | currency }} &mdash;
          please confirm the amount with {{ confirmWithName }} before paying.
        </template>
        <template v-else>
          We couldn't confirm an outstanding balance for this invoice &mdash;
          please confirm the amount with {{ confirmWithName }} before paying.
        </template>
      </div>
    </section>

    <!-- Loading State -->
    <section v-if="loading" class="center mt-25">
      <div class="grey-text">Loading invoice...</div>
    </section>

    <!-- No Payment Options -->
    <section v-else-if="!hasPaymentOptions" class="center mt-25">
      <div class="red-text">
        <i class="fas fa-exclamation-triangle"></i>
        <h5>No payment options available</h5>
      </div>
    </section>

    <!-- Payment Selection (only shown when both methods are enabled) -->
    <section v-else-if="showPaymentSelection">
      <h3 class="bold-font center">Payment {{fetchedAmount | currency}}</h3>

      <div class='large-font mt-5 center'>
        to: {{businessName}}
      </div>

      <div class="center mt-10" v-if="invoice">
        <i class="fas fa-file-invoice grey-text"></i>
        Invoice: <span class="bold-font">{{invoice}}</span>
      </div>

      <div class="center mt-25">
        <h5 class="grey-text">Select Payment Method:</h5>

        <button
          v-if="cardsEnabled"
          @click="selectPaymentMethod('card')"
          class="btn-large wide-btn mt-10">
          <i class="fas fa-credit-card"></i> Pay with Card
        </button>

        <button
          v-if="checksEnabled"
          @click="selectPaymentMethod('check')"
          class="btn-large wide-btn mt-10">
          <i class="fas fa-money-check"></i> Pay with Check
        </button>
      </div>
    </section>

    <!-- Card Payment Component -->
    <section v-else-if="selectedPaymentMethod === 'card'">
      <div v-if="canGoBack" class="mt-10 mb-10">
        <a @click="goBackToSelection" class="pointer">
          <i class="fas fa-arrow-left"></i> Payment methods
        </a>
      </div>
      <by-card
        v-bind="cardProps">
        <template v-slot:default="{ updateToken }">
          <slot :update-token="updateToken"></slot>
        </template>
      </by-card>
    </section>

    <!-- Check Payment Component -->
    <section v-else-if="selectedPaymentMethod === 'check'">
      <div v-if="canGoBack" class="mt-10 mb-10">
        <a @click="goBackToSelection" class="pointer">
          <i class="fas fa-arrow-left"></i> Payment methods
        </a>
      </div>
      <by-check v-bind="checkProps"></by-check>
    </section>

    <!-- Invoice PDF (hidden only when user actively clicks a payment method) -->
    <section v-if="invoiceUrl && !userClickedPayment && !loading" class="center mt-25">
      <div v-if="pdfLoading" class="grey-text mt-10">
        Loading invoice...
      </div>
      <iframe
        v-else-if="invoicePdfUrl"
        :src="invoicePdfUrl"
        class="invoice-pdf-frame">
      </iframe>
      <div v-else-if="pdfError" class="center mt-10 grey-text">
        <i class="fas fa-file-invoice"></i>
        No invoice available
      </div>
    </section>
  </div>
</template>

<script>
import byCard from './by_card'
import byCheck from './by_check'

export default {
  components: { byCard, byCheck },
  props: [
    'presetInvoice',
    'presetEmail',
    'presetPhone',
    'presetAddressZip',
    'presetFirstName',
    'presetLastName',
    'presetNote',
    'amountEditable',
    'invoiceFieldEnabled',
    'noteFieldEnabled',
    'phoneFieldEnabled',
    'emailFieldEnabled',
    'addressFieldEnabled',
    'nameFieldEnabled',
    'requirePolicyConsent',
    'businessName',
    'merchantQid',
    'showTips',
    'checkoutSessionId',
    'custom',
    'policyUrl',
    'useCaptcha',
    'turnstileSiteKey',
    'hideVotes'
  ],
  data() {
    return {
      loading: true,
      fetchedAmount: null,
      balanceStatus: null,
      invoice: this.presetInvoice || null,
      cardsEnabled: false,
      checksEnabled: false,
      selectedPaymentMethod: null,
      invoiceUrl: null,
      invoicePdfUrl: null,
      pdfLoading: false,
      pdfError: false,
      userClickedPayment: false
    }
  },
  created() {
    this.parseUrlParams()
    this.fetchInvoice()
    this.fetchInvoicePdf()
  },
  mounted() {
    // Ensure Font Awesome icons are rendered
    this.$nextTick(() => {
      if (this.$dom) {
        this.$dom.watch()
      }
    })
  },
  methods: {
    parseUrlParams() {
      const urlParams = new URLSearchParams(window.location.search)
      this.cardsEnabled = urlParams.get('cards') === '1'
      this.checksEnabled = urlParams.get('checks') === '1'

      // Get invoice from URL if not provided via prop
      if (!this.invoice) {
        this.invoice = urlParams.get('invoice')
      }

      // Extract invoice_url from hash fragment for PDF rendering
      // Uses simple string replacement instead of URLSearchParams because
      // the invoice URL contains ? and & characters that would be split by URLSearchParams
      var hash = window.location.hash
      if (hash && hash.indexOf('#invoice_url=') === 0) {
        this.invoiceUrl = hash.replace('#invoice_url=', '')
      }
    },
    fetchInvoice() {
      if (!this.invoice) {
        this.notifyError('Invoice number is required')
        this.loading = false
        return
      }

      this.$http.get('/api/v1/public/checkout_sessions/service_titan_invoices', {
        params: {
          merchant_qid: this.merchantQid,
          invoice_number: this.invoice
        }
      }).then(response => {
        const invoiceData = response.body.data

        if (!invoiceData || Object.keys(invoiceData).length === 0) {
          this.notifyError('Failed to fetch invoice')
          // Still show payment form with invoice prepopulated
          this.autoSelectPaymentMethod()
          this.loading = false
          return
        }

        // Set amount from balance, fallback to total when there is nothing to charge
        // against the balance. A zero balance usually means the invoice is already
        // settled, and an absent balance means we simply do not know - both warn
        // instead of silently charging the total, but they must not be conflated:
        // only 'zero' can honestly state a $0.00 balance back to the payer.
        const parsedBalance = parseFloat(invoiceData.balance)
        const balanceKnown = isFinite(parsedBalance)
        const total = parseFloat(invoiceData.total) || 0

        this.balanceStatus = balanceKnown ? (parsedBalance > 0 ? 'due' : 'zero') : 'unknown'
        this.fetchedAmount = balanceKnown && parsedBalance > 0 ? parsedBalance : total

        // Auto-select payment method if only one is available
        this.autoSelectPaymentMethod()

        this.loading = false
      }, error => {
        // Still show payment form with invoice prepopulated
        this.autoSelectPaymentMethod()
        this.loading = false
        this.errorHandler(error)
      })
    },
    selectPaymentMethod(method) {
      this.selectedPaymentMethod = method
      this.userClickedPayment = true
    },
    goBackToSelection() {
      this.selectedPaymentMethod = null
      this.userClickedPayment = false
    },
    fetchInvoicePdf() {
      if (!this.invoiceUrl) return

      this.pdfLoading = true

      var pdfUrl = '/api/v1/public/checkout_sessions/service_titan_invoice_pdf?' +
        'invoice_url=' + encodeURIComponent(this.invoiceUrl) +
        '&merchant_qid=' + encodeURIComponent(this.merchantQid)

      fetch(pdfUrl).then(response => {
        if (response.ok) {
          return response.blob()
        }
        throw new Error('PDF download failed')
      }).then(blob => {
        this.invoicePdfUrl = URL.createObjectURL(blob)
        this.pdfLoading = false
      }).catch(() => {
        this.pdfError = true
        this.pdfLoading = false
      })
    },
    autoSelectPaymentMethod() {
      // If only one payment method is enabled, auto-select it
      if (this.cardsEnabled && !this.checksEnabled) {
        this.selectedPaymentMethod = 'card'
      } else if (this.checksEnabled && !this.cardsEnabled) {
        this.selectedPaymentMethod = 'check'
      }
      // If both are enabled, leave selectedPaymentMethod as null to show selection buttons
    }
  },
  computed: {
    hasPaymentOptions() {
      return this.cardsEnabled || this.checksEnabled
    },
    showBalanceNote() {
      return ['zero', 'unknown'].indexOf(this.balanceStatus) !== -1 &&
        !this.loading && this.hasPaymentOptions
    },
    confirmWithName() {
      return this.businessName || 'the business'
    },
    showPaymentSelection() {
      return this.cardsEnabled && this.checksEnabled && !this.selectedPaymentMethod
    },
    canGoBack() {
      return this.cardsEnabled && this.checksEnabled
    },
    cardProps() {
      return {
        presetAmount: this.fetchedAmount,
        presetInvoice: this.invoice,
        presetEmail: this.presetEmail,
        presetPhone: this.presetPhone,
        presetAddressZip: this.presetAddressZip,
        presetFirstName: this.presetFirstName,
        presetLastName: this.presetLastName,
        presetNote: this.presetNote,
        amountEditable: this.amountEditable,
        invoiceFieldEnabled: this.invoiceFieldEnabled,
        noteFieldEnabled: this.noteFieldEnabled,
        phoneFieldEnabled: this.phoneFieldEnabled,
        emailFieldEnabled: this.emailFieldEnabled,
        addressFieldEnabled: this.addressFieldEnabled,
        nameFieldEnabled: this.nameFieldEnabled,
        requirePolicyConsent: this.requirePolicyConsent,
        businessName: this.businessName,
        merchantQid: this.merchantQid,
        showTips: this.showTips,
        checkoutSessionId: this.checkoutSessionId,
        custom: this.custom,
        policyUrl: this.policyUrl,
        useCaptcha: this.useCaptcha,
        turnstileSiteKey: this.turnstileSiteKey,
        hideVotes: this.hideVotes
      }
    },
    checkProps() {
      return {
        presetAmount: this.fetchedAmount,
        presetInvoice: this.invoice,
        presetNote: this.presetNote,
        amountEditable: this.amountEditable,
        invoiceFieldEnabled: this.invoiceFieldEnabled,
        noteFieldEnabled: this.noteFieldEnabled,
        businessName: this.businessName,
        merchantQid: this.merchantQid,
        showTips: this.showTips,
        checkoutSessionId: this.checkoutSessionId,
        custom: this.custom
      }
    }
  }
}
</script>

<style scoped>
.wide-btn {
  width: 100%;
  max-width: 400px;
}
.balance-note {
  display: inline-block;
  max-width: 400px;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #fafafa;
  text-align: left;
}
.invoice-pdf-frame {
  width: 100%;
  height: 600px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
</style>
